// Memory routes (07 §Webhooks, §Headless rule; 04 §Knowledge, memory, goals, reminders). M7-03
// lands the Recall callback; M7-04 the read-only memory proxy (`memory/search`, `memory/review`),
// `promotions` (list, accept → page, reject) and Setup › Recall's status (`recall/status`).
//
// Recall runs on the VPS only and may be unconfigured (no `pp_` yet) or not yet provisioned for a
// company: every read here says which in `recall` (`RecallConnection`) instead of failing, so a
// screen can say "not connected" honestly. Promotions, decisions and contradictions are CompanyOS
// rows and events (M7-03's callbacks), so the review screen works the same either way.
import { z } from "zod";
import {
  AgentMemoryEvidenceKind,
  AgentMemoryKind,
  MemoryContradictionAction,
  MemoryContradictionState,
  MemoryLaneAction,
  MemoryLaneKind,
  MemoryRuleScope,
  MemoryRuleSource,
  MemoryRuleStatus,
  MemoryScopeKind,
  PlanCheckVerdict,
  ProcedureRunResult,
  PromotionState,
  RecallCaptureLogSource,
  RecallIssuanceState,
  RecallPushState,
} from "../enums";
import {
  Decision,
  Promotion,
  RecallAgentState,
  RecallCallbackAck,
  RecallWebhookDelivery,
} from "../memory-rows";
import { Timestamp, text, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { MemoryQualityView } from "./memory-quality";
import { WritablePageKind } from "./pages";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const memory = { feature: "memory", tag: "memory" } as const;

/**
 * How CompanyOS reaches the company's Recall right now: `connected`; `not_configured` (the API
 * holds no Recall partner key: `RECALL_URL` / `RECALL_PARTNER_TOKEN_SEALED` unset);
 * `not_provisioned` (configured, but the company's tenant or service agent key is not held yet:
 * `recall.provision` runs every minute); `unavailable` (Recall answered with an error just now).
 * `detail` says why in words, never a key.
 */
export const RecallConnection = z
  .object({
    state: z.enum(["connected", "not_configured", "not_provisioned", "unavailable"]),
    detail: z.string().nullable(),
  })
  .meta({ id: "RecallConnection" });
export type RecallConnection = z.infer<typeof RecallConnection>;

/** A memory scope (a Recall bucket's CompanyOS row) with its name; null when it is gone. */
export const MemoryScopeRef = z
  .object({ kind: MemoryScopeKind, id: Uuid, name: z.string().nullable() })
  .meta({ id: "MemoryScopeRef" });
export type MemoryScopeRef = z.infer<typeof MemoryScopeRef>;

/** A promotion as the review screen shows it: its scope's name and whether the caller decides. */
export const PromotionItem = Promotion.extend({
  scope_name: z.string().nullable(),
  can_decide: z.boolean(),
}).meta({ id: "PromotionItem" });
export type PromotionItem = z.infer<typeof PromotionItem>;

/** One side of a contradiction: a Recall record by its type and title. */
export const MemoryRecordRef = z
  .object({ type: z.string(), title: z.string() })
  .meta({ id: "MemoryRecordRef" });

/**
 * A `contradiction.found` Recall delivered (`recall.notice_received`): the two records, the scope
 * of the bucket they sit in, and when it arrived. Read-only in v1: a person settles it in
 * Recall's terms (07 "A person settles these, not a model"); CompanyOS shows it.
 */
export const ContradictionItem = z
  .object({
    id: Uuid.describe("The notice's id (the inbox item's subject)"),
    recall_event_id: z.string(),
    scope: MemoryScopeRef,
    a: MemoryRecordRef,
    b: MemoryRecordRef,
    received_at: Timestamp,
  })
  .meta({ id: "ContradictionItem" });
export type ContradictionItem = z.infer<typeof ContradictionItem>;

/**
 * `GET memory/review`: what waits for the caller. Proposals: pending promotions the caller
 * reviews (a holder of the reviewer seat; with no reviewer seat, the owners; managers see all).
 * Contradictions and decisions: the ones Recall delivered to the caller's inbox (managers: all),
 * newest first. At most 50 of each.
 */
/**
 * MEMORY-HYGIENE: the Recall proposals waiting for the caller's review, as counts (one daily
 * digest per reviewer instead of one inbox item per batch). `exact_merges` can be approved and
 * `from_retired_agents` dismissed in one step (`memory.proposalsBulk`); a proposal nobody reviews
 * expires on its own after `expire_days`.
 */
export const MemoryProposalDigest = z
  .object({
    pending: z.int().min(0),
    exact_merges: z.int().min(0),
    from_retired_agents: z.int().min(0),
    oldest_at: Timestamp.nullable(),
    expire_days: z.int().min(1),
    /** Whether the caller may settle them (a person who reviews them). */
    can_decide: z.boolean(),
    /**
     * MEMORY-RECONCILE: the pending ones grouped, never listed one by one: counts per Recall kind
     * (most first), per scope (most first, at most 20), and the 10 that expire soonest.
     * Contradictions are never in a bulk action: a person settles each one.
     */
    by_kind: z
      .array(z.object({ kind: z.string().min(1).max(64), count: z.int().min(1) }))
      .optional(),
    by_scope: z
      .array(
        z.object({
          scope_kind: MemoryScopeKind,
          scope_id: Uuid,
          name: z.string().nullable(),
          count: z.int().min(1),
        }),
      )
      .max(20)
      .optional(),
    top: z
      .array(
        z.object({
          id: Uuid,
          kind: z.string().min(1).max(64),
          title: z.string().min(1).max(300),
          scope_kind: MemoryScopeKind,
          scope_name: z.string().nullable(),
          exact_merge: z.boolean(),
          expires_at: Timestamp,
        }),
      )
      .max(10)
      .optional(),
  })
  .meta({ id: "MemoryProposalDigest" });
export type MemoryProposalDigest = z.infer<typeof MemoryProposalDigest>;

/** RECALL-CONTRADICTIONS: one side of an open contradiction, as Recall listed it. */
export const MemoryConflictSide = z
  .object({
    type: z.string(),
    title: z.string(),
    /** The record's short text; null when Recall withholds it from this reader. */
    abstract: z.string().nullable(),
    /** When the record last changed (Recall's time). */
    updated: z.string().nullable(),
    /** Who wrote it, by name (a person or an agent). */
    author: z.string().nullable(),
  })
  .meta({ id: "MemoryConflictSide" });
export type MemoryConflictSide = z.infer<typeof MemoryConflictSide>;

/**
 * Recall's settle suggestion for a pair: keep `a` or `b`, `both` (merge, each under a condition)
 * or `neither`; `source` `recall` is its own reading of the pair (a later side moving an earlier
 * one on), `agent` an agent's suggestion. Only a suggestion: a person settles.
 */
export const MemoryConflictSuggestion = z
  .object({
    winner: z.enum(["a", "b", "both", "neither"]),
    category: z.string(),
    reason: z.string().nullable(),
    source: z.enum(["recall", "agent"]),
  })
  .meta({ id: "MemoryConflictSuggestion" });
export type MemoryConflictSuggestion = z.infer<typeof MemoryConflictSuggestion>;

/**
 * RECALL-CONTRADICTIONS: an open contradiction Recall lists in a space the caller reviews, both
 * sides side by side with Recall's suggestion. A person settles it (`memory.conflictSettle`);
 * an agent never does. `recall_note` says why Recall has not taken a verdict yet.
 */
export const MemoryConflictItem = z
  .object({
    id: Uuid,
    scope: MemoryScopeRef,
    pair_id: z.string(),
    a: MemoryConflictSide,
    b: MemoryConflictSide,
    /** How the pair came to be recorded; null when withheld. */
    why: z.string().nullable(),
    suggestion: MemoryConflictSuggestion.nullable(),
    /** How many recorded pairs this one tension stands for. */
    pairs: z.int().min(1),
    since: Timestamp.nullable(),
    state: MemoryContradictionState,
    action: MemoryContradictionAction.nullable(),
    condition: z.string().nullable(),
    decided_at: Timestamp.nullable(),
    /** Recall has the verdict. */
    recall_synced: z.boolean(),
    /** Why Recall has not taken the verdict (its last answer), shown on the pair. */
    recall_note: z.string().nullable(),
    /** When the verdict is sent to Recall again; null when nothing is waiting. */
    recall_retry_at: Timestamp.nullable(),
    can_decide: z.boolean(),
    /**
     * Keeping this side retires the other record, which Recall allows on another person's record
     * only with `retire` on the space: the caller has not got it, so choosing it routes the pair
     * to the space owner instead of settling it.
     */
    keep_a_needs_owner: z.boolean(),
    keep_b_needs_owner: z.boolean(),
    /** A member asked the space owner to pick a winner; it waits in the owner's lane. */
    routed_to_owner: z.boolean(),
    routed_by: z.string().nullable(),
  })
  .meta({ id: "MemoryConflictItem" });
export type MemoryConflictItem = z.infer<typeof MemoryConflictItem>;

/**
 * RECALL-INBOX-LOOP: why a Memory lane card is in this person's lane. `yours`: they hold the seat
 * of the space's department; `served`: the agent it came from serves them; `escalated`: it came
 * up to the owner/CEO (the department has no human, its head is a DE, or nobody answered within
 * the window); `owner_all`: they switched on "See all agents' memory".
 */
export const MemoryLaneWhy = z.enum(["yours", "served", "escalated", "owner_all"]);
export type MemoryLaneWhy = z.infer<typeof MemoryLaneWhy>;

/** A decision waiting on its "did it work" check. */
export const MemoryLaneOutcome = z
  .object({
    decided_on: Timestamp,
    expected: z.string().nullable(),
    check_at: Timestamp,
  })
  .meta({ id: "MemoryLaneOutcome" });

/**
 * One card of the Inbox's Memory lane. `waiting` is the person's own answer kept here until the
 * memory service can take it (`waiting_note` says so); `needs_service` is true when answering
 * would wait. `conflict` carries a contradiction (with Recall's suggestion, when it has one);
 * `promotion` a page candidate (it is accepted into a place of the person's choosing).
 */
export const MemoryLaneItem = z
  .object({
    /** `{kind}:{ref_id}`. */
    id: z.string(),
    kind: MemoryLaneKind,
    ref_id: Uuid,
    title: z.string(),
    detail: z.string().nullable(),
    /** The full text proposed (the title can be cut short); Edit starts from it. */
    proposed_text: z.string().nullable(),
    scope_name: z.string().nullable(),
    /** The agent it came from, by name. */
    from: z.string().nullable(),
    since: Timestamp.nullable(),
    why_here: MemoryLaneWhy,
    state: z.enum(["open", "waiting"]),
    waiting_note: z.string().nullable(),
    can_decide: z.boolean(),
    can_edit: z.boolean(),
    can_bulk: z.boolean(),
    needs_service: z.boolean(),
    conflict: MemoryConflictItem.nullable(),
    promotion: PromotionItem.nullable(),
    outcome: MemoryLaneOutcome.nullable(),
    /**
     * MD-STATUS-FIX: a lesson digest's lessons (one approval per seat per day, up to 10), each
     * kept, edited or rejected with `edit_approve` and `items`; null for any other card.
     */
    lessons: z
      .array(
        z.object({
          write_id: Uuid,
          sentence: z.string(),
          /** Near-identical lessons folded into this one. */
          repeats: z.int().min(0),
        }),
      )
      .nullable()
      .optional(),
  })
  .meta({ id: "MemoryLaneItem" });
export type MemoryLaneItem = z.infer<typeof MemoryLaneItem>;

export const MemoryLane = z
  .object({
    items: z.array(MemoryLaneItem),
    /** Open cards per kind (the lane lists at most 100). */
    counts: z.record(MemoryLaneKind, z.int().min(0)),
    open: z.int().min(0),
    /** The person's own answers kept for the memory service. */
    waiting: z.int().min(0),
    /** The owner/CEO switch: `available` for them only; `on` default false. */
    see_all: z.object({ available: z.boolean(), on: z.boolean() }),
    /** Days an item waits with its department before it also goes to the owner/CEO. */
    escalation_days: z.int().min(1),
    /** Whether the memory service takes a person's answers for the Inbox now (cached, hourly). */
    service_ready: z.boolean(),
  })
  .meta({ id: "MemoryLane" });
export type MemoryLane = z.infer<typeof MemoryLane>;

/**
 * An answer to a lane card: `approve` / `reject` / `edit_approve` (with `text`: the person's own
 * words) for a lesson, a dream or rule proposal; `worked` / `did_not` / `too_early` for a
 * decision's check. A page candidate is accepted with `promotions.accept` (it needs a place);
 * a contradiction with `memory.conflictSettle`. `ui_event_id` is the click's id: Recall audits the
 * answer as "<person> via CompanyOS (id)".
 */
export const MemoryLaneDecide = z
  .strictObject({
    kind: MemoryLaneKind,
    ref_id: Uuid,
    action: MemoryLaneAction,
    text: z.string().trim().min(1).max(2000).optional(),
    reason: z.string().trim().max(500).optional(),
    ui_event_id: z.string().trim().min(1).max(100).optional(),
    /** MD-STATUS-FIX: a lesson digest's answers (`edit_approve`): each lesson kept or not. */
    items: z
      .array(
        z.strictObject({
          write_id: Uuid,
          keep: z.boolean(),
          text: z.string().trim().min(1).max(2000).optional(),
        }),
      )
      .min(1)
      .max(10)
      .optional(),
  })
  .meta({ id: "MemoryLaneDecide" });
export type MemoryLaneDecide = z.infer<typeof MemoryLaneDecide>;

export const MemoryLaneDecided = z
  .object({
    id: z.string(),
    /** `done`: settled here (Recall told by the daily job where it needs to be); `waiting`: kept for the memory service. */
    result: z.enum(["done", "waiting"]),
    note: z.string().nullable(),
  })
  .meta({ id: "MemoryLaneDecided" });
export type MemoryLaneDecided = z.infer<typeof MemoryLaneDecided>;

export const MemoryLaneBulk = z
  .strictObject({ kinds: z.array(MemoryLaneKind).max(8).optional() })
  .meta({ id: "MemoryLaneBulk" });
export type MemoryLaneBulk = z.infer<typeof MemoryLaneBulk>;

export const MemoryLaneBulked = z
  .object({
    approved: z.int().min(0),
    waiting: z.int().min(0),
    /** Left for the person: contradictions, outcomes, and candidates that need a place. */
    skipped: z.int().min(0),
  })
  .meta({ id: "MemoryLaneBulked" });
export type MemoryLaneBulked = z.infer<typeof MemoryLaneBulked>;

export const MemoryLaneSeeAll = z
  .strictObject({ on: z.boolean() })
  .meta({ id: "MemoryLaneSeeAll" });
export type MemoryLaneSeeAll = z.infer<typeof MemoryLaneSeeAll>;

/** Decisions this quarter: how many were checked and how they held up (admin insights). */
export const DecisionCalibration = z
  .object({
    decisions: z.int().min(0),
    checked: z.int().min(0),
    held_up: z.int().min(0),
    did_not: z.int().min(0),
    too_early: z.int().min(0),
    since: Timestamp,
  })
  .meta({ id: "DecisionCalibration" });
export type DecisionCalibration = z.infer<typeof DecisionCalibration>;

export const MemoryConflictSettle = z
  .strictObject({
    action: MemoryContradictionAction,
    /** `merge`: when each side holds, in the person's words (required). */
    condition: z.string().trim().min(1).max(500).optional(),
    note: z.string().trim().max(500).optional(),
  })
  .meta({ id: "MemoryConflictSettle" });
export type MemoryConflictSettle = z.infer<typeof MemoryConflictSettle>;

export const MemoryReview = z
  .object({
    recall: RecallConnection,
    proposals: z.array(PromotionItem),
    contradictions: z.array(ContradictionItem),
    decisions: z.array(Decision),
    proposal_digest: MemoryProposalDigest.optional(),
    /** RECALL-CONTRADICTIONS: open pairs the caller reviews, newest first (at most 50). */
    conflicts: z.array(MemoryConflictItem),
    /** How many are open in all (the list stops at 50). */
    conflicts_open: z.int().min(0),
    /** Settled here but not yet taken by Recall, each with Recall's last answer and the retry time. */
    conflicts_unsent: z.array(MemoryConflictItem),
    /** Spaces the caller manages that could not be checked (Recall did not answer in full). */
    conflicts_unchecked: z.int().min(0),
    /**
     * Spaces the caller manages whose one read came back full (200, Recall's cap; it has no
     * cursor yet): they may hold more contradictions than listed.
     */
    conflicts_possibly_more: z.int().min(0),
  })
  .meta({ id: "MemoryReview" });
export type MemoryReview = z.infer<typeof MemoryReview>;

export const MemoryProposalBulkAction = z.enum(["approve_exact_merges", "dismiss_retired_agents"]);
export const MemoryProposalsBulk = z
  .strictObject({ action: MemoryProposalBulkAction })
  .meta({ id: "MemoryProposalsBulk" });
export type MemoryProposalsBulk = z.infer<typeof MemoryProposalsBulk>;

export const MemoryProposalsSettled = z
  .object({
    action: MemoryProposalBulkAction,
    /** Settled now in CompanyOS (they leave the digest). */
    settled: z.int().min(0),
    digest: MemoryProposalDigest,
  })
  .meta({ id: "MemoryProposalsSettled" });
export type MemoryProposalsSettled = z.infer<typeof MemoryProposalsSettled>;

/** CAPTURE-LESS-NOISE: which conversations go to Recall, by source. */
export const MemoryCaptureSources = z
  .object({ chat: z.boolean(), task_threads: z.boolean(), runs: z.boolean() })
  .meta({ id: "MemoryCaptureSources" });
export type MemoryCaptureSources = z.infer<typeof MemoryCaptureSources>;

/** A company's override: a field set wins over the platform's; null clears it. */
export const MemoryCaptureOverride = z
  .strictObject({
    chat: z.boolean().nullable().optional(),
    task_threads: z.boolean().nullable().optional(),
    runs: z.boolean().nullable().optional(),
  })
  .meta({ id: "MemoryCaptureOverride" });
export type MemoryCaptureOverride = z.infer<typeof MemoryCaptureOverride>;

export const MemoryCaptureSettings = z
  .object({
    /** What applies to this company now. */
    effective: MemoryCaptureSources,
    /** The platform default (an operator setting, stored as data). */
    platform: MemoryCaptureSources,
    /** The company's own choices; null: none. */
    override: z
      .object({
        chat: z.boolean().optional(),
        task_threads: z.boolean().optional(),
        runs: z.boolean().optional(),
      })
      .nullable(),
    /** A conversation is captured once it has been quiet this long (or its task closed). */
    quiet_minutes: z.int().min(1),
    /** The most characters one capture carries (the newest messages are kept). */
    max_chars: z.int().min(1),
  })
  .meta({ id: "MemoryCaptureSettings" });
export type MemoryCaptureSettings = z.infer<typeof MemoryCaptureSettings>;

export const MemoryCaptureDay = z
  .object({
    /** `YYYY-MM-DD` (UTC). */
    day: z.string(),
    source: RecallCaptureLogSource,
    /** Captures sent to Recall. */
    captures: z.int().min(0),
    /** Conversations skipped (no person in them, the source off, or nothing new). */
    skipped: z.int().min(0),
    chars: z.int().min(0),
    /** What Recall wrote from them (saved + updated), once its jobs finished. */
    records: z.int().min(0),
    excerpts: z.int().min(0),
    /** Captures whose job has not reported yet. */
    pending: z.int().min(0),
  })
  .meta({ id: "MemoryCaptureDay" });
export type MemoryCaptureDay = z.infer<typeof MemoryCaptureDay>;

export const MemoryCaptureStats = z
  .object({ days: z.int().min(1), rows: z.array(MemoryCaptureDay) })
  .meta({ id: "MemoryCaptureStats" });
export type MemoryCaptureStats = z.infer<typeof MemoryCaptureStats>;

/**
 * `?scope=`: `company` (every bucket the caller reads), `space:{id}`, `team:{id}`, `seat:{id}`
 * (that bucket), or a subject the side panel follows: `task:{id}` (its space's bucket),
 * `person:{membership}` / `de:{membership}` (every bucket the caller reads, asked by name).
 */
export const MemorySubjectScope = z
  .string()
  .max(64)
  .regex(
    /^(company|(space|team|seat|task|person|de):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i,
    "company, or space|team|seat|task|person|de:{uuid}",
  );

export const MemorySearchQuery = z.object({
  q: z
    .string()
    .trim()
    .max(500)
    .optional()
    .describe("Words to search for; default: the scope's subject's name or title"),
  scope: MemorySubjectScope.optional().describe("Default `company`"),
});

export const MemoryHit = z
  .object({
    scope: MemoryScopeRef,
    /** The record's type and title, its identity in Recall (records have no id). */
    type: z.string(),
    title: z.string(),
    preview: z.string(),
    why: z.string().nullable(),
    /**
     * RECALL-FOLLOWUP: set when Recall found an atom (a sub-record titled `a:<hash>`): its own
     * title. `type` and `title` are then its parent record's (`handles.parent`), which is what
     * the hit shows and opens.
     */
    atom: z.string().nullable(),
  })
  .meta({ id: "MemoryHit" });
export type MemoryHit = z.infer<typeof MemoryHit>;

/**
 * Phase 1 inc 3: how `memory/search` reached Recall for THIS person. The company-wide states of
 * `RecallConnection`, plus `not_set_up`: Recall is connected, but the person cannot be read for
 * yet (this server holds no signing key for person reads, or the person has no provisioned Recall
 * member). Never an empty success: the screen says "Memory isn't set up for you yet".
 */
export const MemorySearchConnection = z
  .object({
    state: z.enum(["connected", "not_configured", "not_provisioned", "unavailable", "not_set_up"]),
    detail: z.string().nullable(),
  })
  .meta({ id: "MemorySearchConnection" });
export type MemorySearchConnection = z.infer<typeof MemorySearchConnection>;

/**
 * `GET memory/search`: Recall's search over the buckets the caller reads (07 §Headless rule:
 * "under the viewer's buckets"). Phase 1 inc 3: read AS the person (the company's install key
 * plus a signed subject assertion for the person's managed Recall member), so Recall enforces
 * the person's own grants; CompanyOS's own rules still pick the buckets first (defence in depth):
 * a bucket the caller does not read is never asked. Personal memory is never searched. At most
 * 200 buckets in one search (chunked by 50), 20 hits; on a refused search, 8 buckets one by one.
 */
export const MemorySearchResult = z
  .object({
    recall: MemorySearchConnection,
    query: z.string(),
    hits: z.array(MemoryHit),
    buckets_searched: z.int().min(0),
  })
  .meta({ id: "MemorySearchResult" });
export type MemorySearchResult = z.infer<typeof MemorySearchResult>;

/**
 * RECALL-BRIEFS: the person's "While you were away" card (design 2026-10-31 §4.1). Built from
 * Recall's catch-up read as the person (their own reach), which needs the person assertion; until
 * Recall offers it (`not_live`) the answer is an empty card, never an error. Shown once per
 * window and closable.
 */
export const CatchUpCard = z
  .object({
    /**
     * `card`: show it. `none`: nothing to show (present recently, nothing new, closed, or already
     * shown for this window). `not_live`: memory cannot read for people yet. `off`: memory is off.
     */
    state: z.enum(["card", "none", "not_live", "off"]),
    card: z
      .object({
        window_key: z.string(),
        from: Timestamp,
        to: Timestamp,
        /** At most 3 spaces, 2 lines each. */
        spaces: z.array(z.object({ name: z.string(), lines: z.array(z.string()) })),
        /** Lines left out ("and N more"). */
        more: z.int().min(0),
        /** Behind (i): what the card is made of, in a line. */
        info: z.string(),
      })
      .nullable(),
  })
  .meta({ id: "CatchUpCard" });
export type CatchUpCard = z.infer<typeof CatchUpCard>;

export const CatchUpDismiss = z
  .strictObject({ window_key: z.string().min(1).max(64) })
  .meta({ id: "CatchUpDismiss" });

export const PromotionListQuery = PageQuery.extend({
  state: PromotionState.optional().describe("Default `pending`"),
});

export const PromotionAccept = z
  .strictObject({
    space_id: Uuid.describe("A live space the caller may write in"),
    title: text(500).optional().describe("Default: the suggested title"),
    kind: WritablePageKind.optional().describe(
      "Default: `sop` for a procedure, `policy` for a rule or constraint, else `page`",
    ),
  })
  .meta({ id: "PromotionAccept" });

export const PromotionReject = z
  .strictObject({ reason: text(2000).optional() })
  .meta({ id: "PromotionReject" });

export const PromotionDecided = z
  .object({ promotion: PromotionItem, page_id: Uuid.nullable() })
  .meta({ id: "PromotionDecided" });

/** A Recall agent key CompanyOS holds (sealed; never shown): whose, and in which state. */
export const RecallAgentKey = z
  .object({
    subject_kind: z.enum(["service_agent", "digital_employee"]),
    subject_id: Uuid,
    name: z.string().nullable(),
    recall_agent_id: z.string().nullable(),
    key_state: RecallIssuanceState,
    has_key: z.boolean(),
    updated_at: Timestamp,
  })
  .meta({ id: "RecallAgentKey" });

/**
 * `GET recall/status` (Setup › Recall): the connection, the company's tenant and install token,
 * the callback's registration (one manual step per tenant, 07 gap 2), the agent keys held, how
 * many members and buckets are linked, and the last reconcile. Never a secret.
 */
export const RecallStatus = z
  .object({
    recall: RecallConnection,
    tenant: z
      .object({
        recall_tenant_id: z.string(),
        slug: z.string().nullable(),
        install_key_state: RecallIssuanceState,
        has_install_key: z.boolean(),
        updated_at: Timestamp,
      })
      .nullable(),
    webhook: z.object({
      registered: z.boolean(),
      recall_hook_id: z.string().nullable(),
      callback_path: z.string(),
      updated_at: Timestamp.nullable(),
    }),
    /**
     * RECALL-FOLLOWUP: how Recall's events reach CompanyOS. `webhook`: a registered callback.
     * `polling`: the worker reads Recall's install feed (`GET /api/install/events`, with the
     * tenant's install key) with a cursor (no webhook can be registered); `state` is the last
     * poll's, `polled_at` its time, `seen` the events read so far. `off`: no tenant yet.
     */
    events: z.object({
      mode: z.enum(["webhook", "polling", "off"]),
      state: z.enum(["ok", "refused", "error", "waiting"]),
      detail: z.string().nullable(),
      polled_at: Timestamp.nullable(),
      seen: z.int().min(0),
    }),
    agents: z.array(RecallAgentKey),
    members: z.int().min(0),
    /**
     * D12 R6: the linked members by the identity Recall's `member.ensure` answered. `local`: a
     * tenant-local user row of its own; `global`: the person's own Recall account, shared with any
     * other tenant they belong to. A link synced before R6 counts `global` (never assumed local).
     */
    member_identity: z.object({ local: z.int().min(0), global: z.int().min(0) }),
    buckets: z.int().min(0),
    last_reconciled_at: Timestamp.nullable(),
    /**
     * Phase 1 inc 3: how people's memory reads are enforced on this server
     * (`RECALL_PERSON_READ_MODE`). `assertion`: Recall checks each read against the person's own
     * grants (a signed subject assertion), `enforced` true. `prefilter`: CompanyOS's service key
     * behind CompanyOS's own bucket filter only (no signing key registered), `enforced` false.
     * `label` is the line Setup › Recall shows.
     */
    person_enforcement: z.object({
      mode: z.enum(["assertion", "prefilter"]),
      enforced: z.boolean(),
      label: z.string(),
    }),
  })
  .meta({ id: "RecallStatus" });
export type RecallStatus = z.infer<typeof RecallStatus>;

/**
 * RECALL-KB: what agents may do in a space synced to Recall: `none`, `read` (Recall `recall`) or
 * `read_write` (Recall `record`: an agent may append to a person's note and replace its own).
 */
export const RecallKbAccess = z.enum(["none", "read", "read_write"]).meta({ id: "RecallKbAccess" });
export type RecallKbAccess = z.infer<typeof RecallKbAccess>;

/** RECALL-KB: one space as a Recall knowledge base: its switch, agent access and last sync. */
export const RecallKbSpace = z
  .object({
    space_id: Uuid,
    name: z.string(),
    kind: z.string(),
    /** Pages the next sync would send (live, not archived, not restricted, not a task's body). */
    pages: z.int().min(0),
    /**
     * "Sync to Recall" (not `enabled`: the isolation suite's boolean sentinels key on names).
     * DOCS-ALWAYS-IN-MEMORY: on by default for every space; off is a manager's opt-out.
     */
    sync_enabled: z.boolean(),
    /** DOCS-ALWAYS-IN-MEMORY: why a manager switched it off (null while on). */
    opt_out_reason: z.string().nullable(),
    agent_access: RecallKbAccess,
    /** Recall's knowledge base, once ensured. */
    slug: z.string().nullable(),
    /** Files Recall holds after the last finished sync. */
    files: z.int().min(0),
    last_synced_at: Timestamp.nullable(),
    last_attempt_at: Timestamp.nullable(),
    /** A "Sync now" waiting for the job's next minute. */
    sync_requested: z.boolean(),
    /** The last sync's paths Recall wanted and got (never their text). */
    last_put: z.array(z.string()),
    last_gone: z.int().min(0).nullable(),
    /** The last refusal: the status and Recall's 4xx body, summarised (never a key). */
    last_error: z.string().nullable(),
    last_error_at: Timestamp.nullable(),
  })
  .meta({ id: "RecallKbSpace" });
export type RecallKbSpace = z.infer<typeof RecallKbSpace>;

export const RecallKbList = z
  .object({
    recall: RecallConnection,
    /** The least minutes between two syncs of one space (`RECALL_KB_SYNC_MS`). */
    interval_minutes: z.int().min(1),
    spaces: z.array(RecallKbSpace),
  })
  .meta({ id: "RecallKbList" });

/**
 * DOCS-ALWAYS-IN-MEMORY: switching a space's sync off needs `opt_out_reason` (1 to 500 characters);
 * a space already off keeps the reason it has when none is sent.
 */
export const RecallKbUpdate = z
  .strictObject({
    sync_enabled: z.boolean(),
    agent_access: RecallKbAccess,
    opt_out_reason: z.string().trim().min(1).max(500).optional(),
  })
  .meta({ id: "RecallKbUpdate" });

/**
 * D8 create/move dialog: the change a person is about to make, as `recall/reach-preview` takes
 * it. A null id names something being created (a new DE, its new seat, a new team).
 */
export const MemoryReachChange = z
  .discriminatedUnion("kind", [
    z.strictObject({
      /** A DE given a seat, moved to another seat, or taken off one. */
      kind: z.literal("seat_holder"),
      membership_id: Uuid.nullable(),
      add_seat_id: Uuid.nullable(),
      end_seat_id: Uuid.nullable(),
      /** The seat it is given (or the new one) also joins this team (the add-DE wizard's team). */
      add_team_id: Uuid.nullable().optional(),
    }),
    z.strictObject({
      /** A seat added to a team, removed from one, or both (a move). */
      kind: z.literal("seat_team"),
      seat_id: Uuid,
      add_team_id: Uuid.nullable(),
      remove_team_id: Uuid.nullable(),
    }),
    z.strictObject({
      /** A team created or edited: its parent (its department) and its lead seat. */
      kind: z.literal("team"),
      team_id: Uuid.nullable(),
      parent_team_id: Uuid.nullable(),
      lead_seat_id: Uuid.nullable(),
    }),
  ])
  .meta({ id: "MemoryReachChange" });
export type MemoryReachChange = z.infer<typeof MemoryReachChange>;

/** One memory an agent reads: the company level, a space, a team, or its own seat. */
export const MemoryReachItem = z
  .object({
    kind: z.enum(["company", "seat", "team", "space"]),
    /** null: the new seat or team the change creates (and the company level). */
    id: Uuid.nullable(),
    name: z.string(),
    /** Its department (a top-level team); null at the company level and for a seat. */
    department: z.object({ id: Uuid.nullable(), name: z.string() }).nullable(),
    access: z.enum(["read", "read_write"]),
  })
  .meta({ id: "MemoryReachItem" });

export const MemoryReachAgent = z
  .object({
    /** null: the DE being created. */
    membership_id: Uuid.nullable(),
    name: z.string(),
    kind: z.enum(["digital_employee", "personal_crew"]),
    before: z.array(MemoryReachItem),
    after: z.array(MemoryReachItem),
    gains: z.array(MemoryReachItem),
    loses: z.array(MemoryReachItem),
  })
  .meta({ id: "MemoryReachAgent" });

export const MemoryReachPreview = z
  .object({ agents: z.array(MemoryReachAgent) })
  .meta({ id: "MemoryReachPreview" });
export type MemoryReachPreview = z.infer<typeof MemoryReachPreview>;

/** D15 (preview): the longest memory sentence an agent may write. */
export const AGENT_MEMORY_SENTENCE_MAX = 280;
/** MEMORY-SKILL-2: the most characters an agent's "why" for a memory write may have. */
export const AGENT_MEMORY_WHY_MAX = 160;

/**
 * D15: one typed memory write. The sentence is checked by the server (one sentence, at most
 * `AGENT_MEMORY_SENTENCE_MAX` characters, not tool output) so each refusal has its own code; the
 * schema bound only stops oversized bodies.
 */
export const AgentMemoryWriteBody = z
  .strictObject({
    kind: AgentMemoryKind,
    sentence: z.string().max(4000),
    /**
     * MEMORY-SKILL-2: why it is worth remembering ("why: ..." or "use when: ..."), required and
     * checked by the server (one line, at most `AGENT_MEMORY_WHY_MAX` characters; a missing one
     * is refused `missing_why`). Optional here only so that refusal has its own code.
     */
    why: z.string().max(4000).optional(),
    evidence: z.strictObject({ kind: AgentMemoryEvidenceKind, id: Uuid }),
  })
  .meta({ id: "AgentMemoryWriteBody" });
export type AgentMemoryWriteBody = z.infer<typeof AgentMemoryWriteBody>;

export const AgentMemoryWritten = z
  .object({
    id: Uuid,
    kind: AgentMemoryKind,
    /**
     * `queued`: on its way to the seat's memory. MEMORY-SKILL-2: a lesson is queued at once too,
     * as an unconfirmed lesson only its own seat is served; `approval_id` names the person's
     * decision that shares it with the team. `proposed` is no longer answered (kept for old rows).
     */
    state: z.enum(["queued", "proposed"]),
    /** A lesson's `memory_lesson` approval (sharing it beyond the seat); null for the other kinds. */
    approval_id: Uuid.nullable(),
    /** MEMORY-SKILL-2: no per-run cap any more (Andrei: no artificial limits); always null. */
    budget: z.null(),
    /** This run's writes so far, this one included. */
    used: z.int().min(1),
    /** MEMORY-SKILL-2: always null (no cap). */
    remaining: z.null(),
    step_id: Uuid,
  })
  .meta({ id: "AgentMemoryWritten" });
export type AgentMemoryWritten = z.infer<typeof AgentMemoryWritten>;

// RECALL-RULES ----------------------------------------------------------------------------------

/** A person or DE as a rule names them. */
export const MemoryRulePerson = z
  .object({
    membership_id: Uuid,
    name: z.string(),
    kind: z.enum(["person", "digital_employee", "other"]),
  })
  .meta({ id: "MemoryRulePerson" });

/**
 * One standing rule. CompanyOS holds it (the source of truth); `recall_state` says whether Recall
 * holds it too, as a Constraint in the company's global space (`not_offered`: this Recall has no
 * door CompanyOS may use yet, `recall_detail` says which). Every run of the DEs it binds is handed
 * it at wake-up either way.
 */
export const MemoryRuleView = z
  .object({
    id: Uuid,
    scope: MemoryRuleScope,
    /** The DE a `de` rule binds; null for a company rule. */
    de: MemoryRulePerson.nullable(),
    title: z.string(),
    text: z.string(),
    why: z.string().nullable(),
    source: MemoryRuleSource,
    status: MemoryRuleStatus,
    proposed_by: MemoryRulePerson.nullable(),
    /** A proposal's `memory_rule` approval (decided in the Inbox). */
    approval_id: Uuid.nullable(),
    decided_by: MemoryRulePerson.nullable(),
    decided_at: Timestamp.nullable(),
    lifted_at: Timestamp.nullable(),
    recall_state: RecallPushState,
    recall_detail: z.string().nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "MemoryRuleView" });
export type MemoryRuleView = z.infer<typeof MemoryRuleView>;

/**
 * B1-DE-RULES: a board automation a DE owns, as its page lists it beside its standing rules (the
 * automation stays in Work › Automations; nothing is copied).
 */
export const DeAutomationRuleRef = z
  .object({
    id: Uuid,
    name: z.string(),
    board_id: Uuid,
    board_name: z.string(),
    enabled: z.boolean(),
  })
  .meta({ id: "DeAutomationRuleRef" });

export const MemoryRuleList = z
  .object({
    data: z.array(MemoryRuleView),
    /** With `de_id`: the board automations that DE owns (boards the caller can view). */
    automation_rules: z.array(DeAutomationRuleRef),
    /** The caller states and lifts rules directly (an owner or admin); others propose. */
    can_manage: z.boolean(),
  })
  .meta({ id: "MemoryRuleList" });
export type MemoryRuleList = z.infer<typeof MemoryRuleList>;

export const MemoryRuleListQuery = z.object({
  de_id: Uuid.optional().describe(
    "The rules that bind this DE: the company's and its own (and its board automations)",
  ),
  status: z
    .enum(["current", "all"])
    .optional()
    .describe("`current` (default): active and proposed; `all`: lifted and dismissed too"),
});

export const MemoryRuleCreate = z
  .strictObject({
    scope: MemoryRuleScope,
    /** The DE a `de` rule binds (its membership id); absent for a company rule. */
    de_id: Uuid.optional(),
    /** A short name (absent: the start of the text). */
    title: z.string().trim().min(1).max(120).optional(),
    text: z.string().trim().min(1).max(1000),
    why: z.string().trim().min(1).max(1000).optional(),
    /** Where it was stated: Setup, a DE's page, or a chat message (`message_id`). */
    source: z.enum(["setup", "de_page", "chat"]),
    message_id: Uuid.optional(),
  })
  .meta({ id: "MemoryRuleCreate" });
export type MemoryRuleCreate = z.infer<typeof MemoryRuleCreate>;

export const MemoryRuleLift = z
  .strictObject({ reason: z.string().trim().min(1).max(500).optional() })
  .meta({ id: "MemoryRuleLift" });

/** `suggest_rule`: a DE proposes a standing rule for a person to approve. */
export const AgentRuleSuggestion = z
  .strictObject({
    /** `self`: a rule for this DE only; `company`: for every agent. */
    scope: z.enum(["self", "company"]),
    title: z.string().trim().min(1).max(120).optional(),
    text: z.string().trim().min(1).max(1000),
    /** What it rests on: what happened and why the rule would help (one or two lines). */
    why: z.string().trim().min(1).max(1000),
  })
  .meta({ id: "AgentRuleSuggestion" });
export type AgentRuleSuggestion = z.infer<typeof AgentRuleSuggestion>;

export const AgentRuleSuggested = z
  .object({
    rule_id: Uuid,
    state: z.literal("proposed"),
    approval_id: Uuid,
    step_id: Uuid,
  })
  .meta({ id: "AgentRuleSuggested" });

// RECALL-PROCEDURES -----------------------------------------------------------------------------

/** One procedure that fits a task: from Recall, or a playbook this DE follows. */
export const ProcedureOffer = z
  .object({
    title: z.string(),
    /** `recall`: Recall's `procedure_for` matched it; `playbook`: a playbook of this DE's seat. */
    source: z.enum(["recall", "playbook"]),
    /** Recall's version digest (quote it in `procedure_run`); null for a playbook not in Recall. */
    version: z.string().nullable(),
    bucket_id: z.string().nullable(),
    summary: z.string().nullable(),
    preconditions: z.array(z.string()),
    steps: z.array(z.string()),
    checks: z.array(z.string()),
    aborts: z.array(z.string()),
    /** Recall's one-line run rates ("9 runs, 8 succeeded; step 3 failed twice"). */
    rates: z.string().nullable(),
    /** The playbook this procedure is, when it is one. */
    playbook: z.object({ page_id: Uuid, title: z.string() }).nullable(),
  })
  .meta({ id: "ProcedureOffer" });

export const AgentProcedureFor = z
  .strictObject({ task: z.string().trim().min(2).max(2000) })
  .meta({ id: "AgentProcedureFor" });

export const AgentProcedureOffers = z
  .object({
    procedures: z.array(ProcedureOffer),
    /** Whether Recall answered (false: only playbooks were searched; `detail` says why). */
    recall: z.object({ answered: z.boolean(), detail: z.string().nullable() }),
    hint: z.string(),
  })
  .meta({ id: "AgentProcedureOffers" });

export const AgentProcedureRunBody = z
  .strictObject({
    procedure: z.string().trim().min(1).max(300),
    /** The version `procedure_for` gave (Recall's digest); absent for a playbook not in Recall. */
    version: z.string().trim().min(1).max(64).optional(),
    bucket_id: z.string().trim().min(1).max(64).optional(),
    result: ProcedureRunResult,
    steps: z
      .array(
        z.strictObject({
          n: z.int().min(1).max(200),
          result: z.enum(["done", "failed", "skipped"]),
          note: z.string().trim().min(1).max(500).optional(),
        }),
      )
      .max(100)
      .default([]),
    task: z.string().trim().min(1).max(500),
    note: z.string().trim().min(1).max(2000).optional(),
  })
  .meta({ id: "AgentProcedureRunBody" });
export type AgentProcedureRunBody = z.infer<typeof AgentProcedureRunBody>;

export const ProcedureRunView = z
  .object({
    id: Uuid,
    procedure_title: z.string(),
    page_id: Uuid.nullable(),
    by: MemoryRulePerson,
    run_id: Uuid.nullable(),
    task: z.string(),
    result: ProcedureRunResult,
    steps: z.array(
      z.object({ n: z.int(), result: z.string(), note: z.string().nullable().optional() }),
    ),
    note: z.string().nullable(),
    recall_state: RecallPushState,
    recall_detail: z.string().nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "ProcedureRunView" });
export type ProcedureRunView = z.infer<typeof ProcedureRunView>;

export const AgentProcedureRunRecorded = ProcedureRunView.extend({ step_id: Uuid }).meta({
  id: "AgentProcedureRunRecorded",
});

// RECALL-RUN-LIFECYCLE ------------------------------------------------------------------------

/** `POST agent/memory/wake`: nothing to send; the run names everything. */
export const AgentMemoryWakeBody = z.strictObject({}).meta({ id: "AgentMemoryWakeBody" });

/**
 * What memory hands a run at wake: one brief about the run's subject (Recall's context, at most
 * the operator's `memory.brief_chars`), where the DE left off (its working state, under 7 days),
 * and the procedure its task's kind maps to. `text` is the block a runtime puts in the prompt
 * (advisory data, never instructions); empty when memory had nothing.
 */
export const AgentMemoryWake = z
  .object({
    run_id: Uuid,
    subject: z.string(),
    brief: z.object({
      answered: z.boolean(),
      chars: z.int().min(0),
      budget: z.int().min(0),
      detail: z.string().nullable(),
      /**
       * WAKE-READ-FIX: where the brief came from. `read`: Recall's `/api/context` was asked for
       * this run; `reused`: the brief of `reused_from` (same DE, request and subject, minutes ago),
       * no read; `failed`: the read was made and did not answer; `off`: no read (no memory or key).
       */
      source: z.enum(["read", "reused", "failed", "off"]).optional(),
      reused_from: Uuid.nullable().optional(),
    }),
    resume: z.object({ found: z.boolean(), age_days: z.number().nullable() }),
    procedure: ProcedureOffer.nullable(),
    text: z.string(),
  })
  .meta({ id: "AgentMemoryWake" });
export type AgentMemoryWake = z.infer<typeof AgentMemoryWake>;

/** `POST agent/memory/carry`: a run ending with work left (a timeout) leaves a carry note. */
export const AgentMemoryCarryBody = z
  .strictObject({
    reason: z.enum(["timeout", "lost", "compaction"]),
    /** What the run had done, in the runtime's words (at most 600 characters kept). */
    summary: z.string().trim().max(4000).optional(),
  })
  .meta({ id: "AgentMemoryCarryBody" });

export const AgentMemoryCarried = z
  .object({
    saved: z.boolean(),
    project: z.string(),
    detail: z.string().nullable(),
  })
  .meta({ id: "AgentMemoryCarried" });

// RECALL-UPKEEP ---------------------------------------------------------------------------------

/**
 * Setup › Recall › Memory upkeep: the dream (nightly consolidation) and hot copy settings as saved
 * (`settings.memory.dreams`, `settings.memory.hot_copy`; set with `PATCH companies/{co}`), and
 * whether Recall took the dream schedule (`not_offered`: Recall has no door the company's install
 * key may set it through yet).
 */
export const RecallUpkeep = z
  .object({
    dreams: z.object({
      enabled: z.boolean(),
      hour_utc: z.int().min(0).max(23),
      auto_apply_topics: z.boolean(),
      recall_state: RecallPushState,
      recall_detail: z.string().nullable(),
      pushed_at: Timestamp.nullable(),
    }),
    hot_copy: z.object({ enabled: z.boolean() }),
    /** Whether the company's space in Recall is its global space, where rules are set. */
    rules_space: z.object({ state: RecallPushState, detail: z.string().nullable() }),
  })
  .meta({ id: "RecallUpkeep" });
export type RecallUpkeep = z.infer<typeof RecallUpkeep>;
/** PLAN-CHECK: the plan a DE is about to act on, in one or two sentences. */
export const AgentPlanCheckBody = z
  .strictObject({
    plan: z.string().trim().min(1).max(2000),
    /** How many records per space are weighed (Recall's `k`, default 6). */
    k: z.int().min(1).max(10).optional(),
  })
  .meta({ id: "AgentPlanCheckBody" });
export type AgentPlanCheckBody = z.infer<typeof AgentPlanCheckBody>;

export const AgentPlanChecked = z
  .object({
    check_id: Uuid,
    /**
     * The worst verdict over the spaces asked: `conflict` (stop and ask your human), `caution`,
     * `no_conflict_found`, `unjudged`; `unavailable` when Recall could not be asked.
     */
    verdict: PlanCheckVerdict,
    conflicts: z.array(
      z.object({
        type: z.string(),
        title: z.string(),
        why: z.string().nullable(),
        space: z.string().nullable(),
      }),
    ),
    guidance: z.string().nullable(),
    spaces_checked: z.int().min(0),
    detail: z.string().nullable(),
    step_id: Uuid,
  })
  .meta({ id: "AgentPlanChecked" });
export type AgentPlanChecked = z.infer<typeof AgentPlanChecked>;

// RECALL-ADMIN-MEMORY ---------------------------------------------------------------------------
// Setup › Memory (owners and admins): Review (the Memory lane, in bulk), Browse, Spaces, Rules,
// Health. Everything that needs the memory service's `admin` act (not live yet, §7.1 (A)) says so
// in `service_ready` / `waiting_note` and is shown from what CompanyOS holds meanwhile, never as
// an error; an action that needs it answers `waiting` and changes nothing.

/** What the admin act's absence says, in words. */
export const MEMORY_ADMIN_WAITING = "Waiting on memory service";

export const MemoryAdminSpaceKind = z.enum(["company", "team", "space", "agent"]);
export type MemoryAdminSpaceKind = z.infer<typeof MemoryAdminSpaceKind>;

/** One space Browse can open: `company`, `seat:{id}`, `team:{id}` or `space:{id}`. */
export const MemoryAdminSpace = z
  .object({
    key: z.string(),
    name: z.string(),
    kind: MemoryAdminSpaceKind,
    /** The agent whose space it is (`DE` or `AG`), for an agent space. */
    agent: z.object({ id: Uuid, name: z.string(), label: z.enum(["DE", "AG"]) }).nullable(),
    /** The people who hold the seat, by name. */
    owners: z.array(z.string()),
    records: z.int().min(0),
    /** False when the count is not known yet (a shared space before the memory service answers). */
    counted: z.boolean(),
    by_type: z.record(z.string(), z.int().min(0)),
    /** Why the caller sees it: `shared` (a shared space) or the lane's reasons. */
    why_here: z.enum(["shared", "yours", "served", "escalated", "owner_all"]),
  })
  .meta({ id: "MemoryAdminSpace" });
export type MemoryAdminSpace = z.infer<typeof MemoryAdminSpace>;

export const MemoryAdminRecordState = z.enum(["live", "queued", "proposed", "flagged", "archived"]);

/** One record Browse lists. `key` is opaque: pass it back to the record page. */
export const MemoryAdminRecord = z
  .object({
    key: z.string(),
    space_key: z.string(),
    space_name: z.string(),
    type: z.string(),
    title: z.string(),
    /** The record's words, cut short (the page has the rest). */
    text: z.string(),
    /** The agent or person who wrote it, by name. */
    by: z.string().nullable(),
    at: Timestamp.nullable(),
    state: MemoryAdminRecordState,
    /** `memory`: read from the memory service; `companyos`: what agents told memory, kept here. */
    source: z.enum(["memory", "companyos"]),
  })
  .meta({ id: "MemoryAdminRecord" });
export type MemoryAdminRecord = z.infer<typeof MemoryAdminRecord>;

export const MemoryAdminSeeAll = z.object({ available: z.boolean(), on: z.boolean() });

export const MemoryAdminBrowseQuery = z.object({
  space: z.string().trim().max(100).optional().describe("A space key from the list; default: all"),
  type: z.string().trim().max(60).optional(),
  q: z.string().trim().max(200).optional(),
  topic: z.string().trim().max(120).optional(),
  cursor: z.string().trim().max(100).optional(),
});

export const MemoryAdminBrowse = z
  .object({
    /** Whether the memory service takes the admin's reads (the `admin` act). */
    service_ready: z.boolean(),
    waiting_note: z.string().nullable(),
    see_all: MemoryAdminSeeAll,
    /** Where the records come from: the memory service, or what CompanyOS holds meanwhile. */
    source: z.enum(["memory", "companyos"]),
    spaces: z.array(MemoryAdminSpace),
    /** The space asked for (`null`: all the caller sees). */
    space: z.string().nullable(),
    /** The open space's topics with counts (the map), when the memory service gave them. */
    topics: z.array(z.object({ topic: z.string(), count: z.int().min(0) })),
    records: z.array(MemoryAdminRecord),
    next_cursor: z.string().nullable(),
    /** The memory service did not answer just now; what is shown is what CompanyOS holds. */
    unavailable: z.string().nullable(),
  })
  .meta({ id: "MemoryAdminBrowse" });
export type MemoryAdminBrowse = z.infer<typeof MemoryAdminBrowse>;

/** A record's key is its type and title percent-encoded: room for a 300-character title. */
export const MEMORY_ADMIN_KEY_MAX = 3000;
export const MemoryAdminRecordQuery = z.object({
  key: z.string().trim().min(1).max(MEMORY_ADMIN_KEY_MAX),
});

export const MemoryAdminVersion = z
  .object({
    n: z.int().min(1),
    at: Timestamp.nullable(),
    by: z.string().nullable(),
    text: z.string(),
    reason: z.string().nullable(),
  })
  .meta({ id: "MemoryAdminVersion" });

export const MemoryAdminTraceRow = z
  .object({
    at: Timestamp.nullable(),
    kind: z.string(),
    by: z.string().nullable(),
    note: z.string().nullable(),
  })
  .meta({ id: "MemoryAdminTraceRow" });

export const MemoryAdminLink = z
  .object({
    /** The linked record's key, when it can be opened. */
    key: z.string().nullable(),
    type: z.string(),
    title: z.string(),
    edge: z.string(),
  })
  .meta({ id: "MemoryAdminLink" });

export const MemoryAdminChain = z
  .object({
    kind: z.string(),
    steps: z.array(z.object({ type: z.string(), title: z.string(), at: Timestamp.nullable() })),
  })
  .meta({ id: "MemoryAdminChain" });

export const MemoryAdminTimelineRow = z
  .object({
    at: Timestamp.nullable(),
    type: z.string(),
    title: z.string(),
    note: z.string().nullable(),
  })
  .meta({ id: "MemoryAdminTimelineRow" });

/** One record with everything around it: versions, trace, expansion, chains and timeline. */
export const MemoryAdminRecordPage = z
  .object({
    record: MemoryAdminRecord,
    /** The record's whole text. */
    full_text: z.string(),
    reason: z.string().nullable(),
    /** What it rests on (a run, a task, a message), in words. */
    evidence: z.string().nullable(),
    versions: z.array(MemoryAdminVersion),
    trace: z.array(MemoryAdminTraceRow),
    expand: z.array(MemoryAdminLink),
    chains: z.array(MemoryAdminChain),
    timeline: z.array(MemoryAdminTimelineRow),
    service_ready: z.boolean(),
    /** Set when part of the page needs the memory service (versions, expansion, chains). */
    waiting_note: z.string().nullable(),
    /** Whether flag / archive / restore can be done now (the install doors, live). */
    can_act: z.boolean(),
  })
  .meta({ id: "MemoryAdminRecordPage" });
export type MemoryAdminRecordPage = z.infer<typeof MemoryAdminRecordPage>;

export const MemoryAdminRecordAct = z
  .strictObject({
    key: z.string().trim().min(1).max(MEMORY_ADMIN_KEY_MAX),
    action: z.enum(["flag", "archive", "restore"]),
    ui_event_id: z.string().trim().min(1).max(100).optional(),
  })
  .meta({ id: "MemoryAdminRecordAct" });

/** The answer to an admin act: done, kept for the memory service, or refused with the reason. */
export const MemoryAdminActed = z
  .object({
    result: z.enum(["done", "waiting", "refused"]),
    note: z.string().nullable(),
    state: MemoryAdminRecordState.nullable(),
  })
  .meta({ id: "MemoryAdminActed" });
export type MemoryAdminActed = z.infer<typeof MemoryAdminActed>;

export const MemoryAdminOverview = z
  .object({
    service_ready: z.boolean(),
    waiting_note: z.string().nullable(),
    see_all: MemoryAdminSeeAll,
    /** Open cards in the caller's Memory lane (the Review tab). */
    review_open: z.int().min(0),
    jobs_open: z.int().min(0),
  })
  .meta({ id: "MemoryAdminOverview" });

export const MemoryAdminWho = z
  .object({
    name: z.string(),
    kind: z.enum(["person", "agent"]),
    topics: z.array(z.string()),
    writes: z.int().min(0),
  })
  .meta({ id: "MemoryAdminWho" });

export const MemoryAdminSpaceRow = MemoryAdminSpace.extend({
  /** The space's charter; null while none is set or the memory service cannot say. */
  charter: z.string().nullable(),
  /** What agents may do in it; null when the memory service cannot say. */
  agent_access: z.enum(["none", "recall", "record"]).nullable(),
  who_knows: z.array(MemoryAdminWho),
  /** `memory`: from the memory service; `companyos`: from what agents told memory. */
  knows_source: z.enum(["memory", "companyos"]),
  /** Whether the caller may edit the charter and agent access (needs the memory service too). */
  can_tend: z.boolean(),
}).meta({ id: "MemoryAdminSpaceRow" });

export const MemoryAdminSpaces = z
  .object({
    service_ready: z.boolean(),
    waiting_note: z.string().nullable(),
    spaces: z.array(MemoryAdminSpaceRow),
  })
  .meta({ id: "MemoryAdminSpaces" });
export type MemoryAdminSpaces = z.infer<typeof MemoryAdminSpaces>;

export const MemoryAdminCharterSet = z
  .strictObject({
    text: z.string().trim().min(1).max(2000),
    ui_event_id: z.string().trim().min(1).max(100).optional(),
  })
  .meta({ id: "MemoryAdminCharterSet" });

export const MemoryAdminAccessSet = z
  .strictObject({
    agent_access: z.enum(["none", "recall", "record"]),
    ui_event_id: z.string().trim().min(1).max(100).optional(),
  })
  .meta({ id: "MemoryAdminAccessSet" });

export const MemoryAdminSpaceParams = CompanyParams.extend({
  space: z.string().trim().min(1).max(100),
});

export const MemoryAdminRuleItem = z
  .object({
    type: z.string(),
    title: z.string(),
    text: z.string().nullable(),
  })
  .meta({ id: "MemoryAdminRuleItem" });

/** One space's rule furniture from the memory service. */
export const MemoryAdminRulesSpace = z
  .object({
    key: z.string(),
    name: z.string(),
    always_on: z.array(MemoryAdminRuleItem.extend({ order: z.int().min(1) })),
    always_on_limit: z.int().min(1),
    /** The space's standing rules (Constraints) with their fence. */
    rules: z.array(
      MemoryAdminRuleItem.extend({
        applies_to: z.array(z.string()),
        except: z.array(z.string()),
      }),
    ),
    procedures: z.array(
      z.object({
        title: z.string(),
        steps: z.array(z.object({ n: z.int().min(1), text: z.string(), vetoed: z.boolean() })),
      }),
    ),
  })
  .meta({ id: "MemoryAdminRulesSpace" });

export const MemoryAdminRules = z
  .object({
    service_ready: z.boolean(),
    waiting_note: z.string().nullable(),
    spaces: z.array(MemoryAdminRulesSpace),
    /** The memory service did not answer for these spaces just now. */
    unavailable: z.array(z.string()),
    /** Some spaces or rules are left out (the first of each are shown). */
    truncated: z.boolean(),
  })
  .meta({ id: "MemoryAdminRules" });
export type MemoryAdminRules = z.infer<typeof MemoryAdminRules>;

export const MemoryAdminRuleAct = z
  .strictObject({
    space: z.string().trim().min(1).max(100),
    op: z.enum([
      "always_on_add",
      "always_on_remove",
      "always_on_up",
      "always_on_down",
      "retire",
      "lift",
      "veto",
      "unveto",
    ]),
    /** A record's type and title (always-on, retire, lift); a procedure's title (veto). */
    type: z.string().trim().min(1).max(60).optional(),
    title: z.string().trim().min(1).max(300),
    step: z.int().min(1).max(200).optional(),
    reason: z.string().trim().max(500).optional(),
    ui_event_id: z.string().trim().min(1).max(100).optional(),
  })
  .meta({ id: "MemoryAdminRuleAct" });

export const MemoryAdminJob = z
  .object({
    id: Uuid,
    kind: z.enum(["batch_settle", "clear_injection_flags"]),
    space_key: z.string(),
    space_name: z.string().nullable(),
    /** `batch_settle`: how the pairs are to be settled, and how many there were. */
    mode: z.enum(["keep_newer", "dismiss"]).nullable(),
    pairs: z.int().min(0).nullable(),
    state: z.enum(["open", "done", "failed"]),
    filed_by: z.string().nullable(),
    filed_at: Timestamp,
    closed_at: Timestamp.nullable(),
    note: z.string().nullable(),
  })
  .meta({ id: "MemoryAdminJob" });
export type MemoryAdminJob = z.infer<typeof MemoryAdminJob>;

export const MemoryAdminJobFile = z
  .strictObject({
    kind: z.enum(["batch_settle", "clear_injection_flags"]),
    /** A space key; for `batch_settle` also `all` (every open pair the caller sees). */
    space: z.string().trim().min(1).max(100),
    mode: z.enum(["keep_newer", "dismiss"]).optional(),
    ui_event_id: z.string().trim().min(1).max(100).optional(),
  })
  .meta({ id: "MemoryAdminJobFile" });

/** Filing a job: filed, or why nothing was filed (never an error). */
export const MemoryAdminJobFiled = z
  .object({
    result: z.enum(["filed", "waiting", "nothing"]),
    note: z.string().nullable(),
    job: MemoryAdminJob.nullable(),
  })
  .meta({ id: "MemoryAdminJobFiled" });
export type MemoryAdminJobFiled = z.infer<typeof MemoryAdminJobFiled>;

export const MemoryAdminJobs = z
  .object({ jobs: z.array(MemoryAdminJob) })
  .meta({ id: "MemoryAdminJobs" });

export const MemoryAdminHealth = z
  .object({
    service_ready: z.boolean(),
    waiting_note: z.string().nullable(),
    /** What agents told memory, last 30 days. */
    writes: z.object({
      sent: z.int().min(0),
      queued: z.int().min(0),
      proposed: z.int().min(0),
      rejected: z.int().min(0),
    }),
    /** Saves waiting to reach the memory service, and why some are held. */
    outbox: z.object({
      pending: z.int().min(0),
      held: z.int().min(0),
      by_reason: z.array(z.object({ reason: z.string(), count: z.int().min(0) })),
    }),
    lane_open: z.int().min(0),
    answers_waiting: z.int().min(0),
    contradictions_open: z.int().min(0),
    /** Per space, from the memory service; null while it cannot say. */
    spaces: z.array(
      z.object({
        key: z.string(),
        name: z.string(),
        records: z.int().min(0),
        stale: z.int().min(0).nullable(),
        duplicates: z.int().min(0).nullable(),
        never_recalled: z.int().min(0).nullable(),
        injections: z.int().min(0).nullable(),
      }),
    ),
    burden: z
      .object({
        waiting: z.int().min(0),
        oldest_days: z.int().min(0).nullable(),
        answered_week: z.int().min(0),
      })
      .nullable(),
    inert_grants: z.array(
      z.object({ agent: z.string(), space: z.string(), reason: z.string().nullable() }),
    ),
    injection_flags: z.array(
      z.object({
        space_key: z.string(),
        space_name: z.string(),
        type: z.string(),
        title: z.string(),
        at: Timestamp.nullable(),
        reason: z.string().nullable(),
      }),
    ),
    jobs: z.array(MemoryAdminJob),
    /** MEMORY-QUALITY-WATCH: the hourly watch's latest numbers, from what CompanyOS holds. */
    quality: MemoryQualityView,
  })
  .meta({ id: "MemoryAdminHealth" });
export type MemoryAdminHealth = z.infer<typeof MemoryAdminHealth>;

export const memoryRoutes = {
  "recall.callbacks": defineRoute({
    feature: "memory",
    id: "recall.callbacks",
    method: "post",
    path: `${base}/recall/callbacks`,
    tag: "memory",
    summary: "Recall webhook delivery (signed); recorded once per Recall event id",
    description:
      "Called by the company's Recall tenant, never by a person. `X-Recall-Signature: " +
      't=<s>,sha256=<HMAC-SHA256 of "<s>." + body>` under the tenant\'s webhook secret (held ' +
      "sealed in `recall_link`, kind `webhook`; 300 s tolerance) is the credential: a missing " +
      "or wrong signature is 401 before anything else is read. Then the `memory` feature gate, " +
      "then a per-company write budget. `proposal.waiting`, `decision.outcome` and " +
      "`contradiction.found` are recorded as `recall.notice_received` (subject: a uuid derived " +
      "from the tenant and the signed body's id) for the `recall.callbacks` consumer; a second " +
      "delivery of the same id answers `duplicate` and writes nothing. Every other kind is " +
      "logged and answered `ignored`.",
    access: "recall_signature",
    params: CompanyParams,
    body: RecallWebhookDelivery,
    response: { status: 202, schema: RecallCallbackAck },
    errors: ["payload_too_large"],
    emits: ["recall.notice_received"],
  }),
  // M7-04 ----------------------------------------------------------------------------------
  "memory.review": defineRoute({
    ...memory,
    id: "memory.review",
    method: "get",
    path: `${base}/memory/review`,
    summary: "Memory proposals, contradictions and decisions waiting for the caller",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: MemoryReview },
    errors: [],
    emits: [],
  }),
  "memory.proposalsBulk": defineRoute({
    ...memory,
    id: "memory.proposalsBulk",
    method: "post",
    path: `${base}/memory/proposals/bulk`,
    summary:
      "Settle many memory proposals at once: approve every exact merge, or dismiss every one from a retired agent",
    description:
      "MEMORY-HYGIENE. Only the proposals the caller reviews (a person holding the reviewer " +
      "seat; owners and admins for every one) that are still pending. `approve_exact_merges` " +
      "approves each merge of two identical records; `dismiss_retired_agents` dismisses each one " +
      "a retired or removed DE's memory proposed. They leave the caller's digest at once and are " +
      "told to Recall by the daily `memory.review_digest` job.",
    access: "member",
    params: CompanyParams,
    body: MemoryProposalsBulk,
    idempotency: "optional",
    response: { status: 200, schema: MemoryProposalsSettled },
    errors: ["forbidden"],
    emits: ["memory_proposals.settled"],
  }),
  "memory.captureSettings": defineRoute({
    ...memory,
    id: "memory.captureSettings",
    method: "get",
    path: `${base}/memory/capture`,
    summary:
      "Which conversations go to Recall, by source: the effective policy, its default and the company's override",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: MemoryCaptureSettings },
    errors: [],
    emits: [],
  }),
  "memory.captureUpdate": defineRoute({
    ...memory,
    id: "memory.captureUpdate",
    method: "put",
    path: `${base}/memory/capture`,
    summary: "Set the company's capture override by source (null clears a field)",
    description:
      "CAPTURE-LESS-NOISE. `chat` (topics in shared channels), `task_threads` (task comments) " +
      "and `runs` (run threads). A conversation is captured once it is quiet, as one ingest, only " +
      "when a person took part; agent-only conversations never are.",
    access: "manager",
    params: CompanyParams,
    body: MemoryCaptureOverride,
    response: { status: 200, schema: MemoryCaptureSettings },
    errors: [],
    emits: ["company.updated"],
  }),
  "memory.captureStats": defineRoute({
    ...memory,
    id: "memory.captureStats",
    method: "get",
    path: `${base}/memory/capture/stats`,
    summary: "Captures and the records Recall wrote from them, per day and source (last 14 days)",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: MemoryCaptureStats },
    errors: [],
    emits: [],
  }),
  // RECALL-CONTRADICTIONS: a person settles one open contradiction
  "memory.conflictSettle": defineRoute({
    ...memory,
    id: "memory.conflictSettle",
    method: "post",
    path: `${base}/memory/conflicts/{id}/settle`,
    summary: "Settle one memory contradiction: keep a side, merge, or dismiss",
    description:
      "A person who reviews the pair's space (the scope's reviewer seat; owners and admins for " +
      "every one) settles it; a digital employee, a crew or a key never does (403). `keep_a` / " +
      "`keep_b`: the other side is superseded; `merge`: both stay, each under `condition` (when " +
      "each holds, required); `dismiss`: not a real conflict. Only an open pair (409 otherwise). " +
      "CompanyOS keeps the verdict at once; the `memory.contradictions` job tells Recall as " +
      "this person when Recall lets CompanyOS settle (`recall_note` says when it has not yet).",
    access: "member",
    params: CompanyItemParams,
    body: MemoryConflictSettle,
    idempotency: "optional",
    response: { status: 200, schema: MemoryConflictItem },
    errors: ["forbidden", "conflict"],
    emits: ["memory_contradiction.settled"],
  }),
  "memory.search": defineRoute({
    ...memory,
    id: "memory.search",
    method: "get",
    path: `${base}/memory/search`,
    summary: "What Recall knows, searched over the buckets the caller reads (read-only proxy)",
    access: "member",
    params: CompanyParams,
    query: MemorySearchQuery,
    response: { status: 200, schema: MemorySearchResult },
    errors: [],
    emits: [],
  }),
  "memory.catchUp": defineRoute({
    ...memory,
    id: "memory.catchUp",
    method: "get",
    path: `${base}/me/catch-up`,
    summary:
      "While you were away: the caller's catch-up card (empty until memory can read for them)",
    description:
      "RECALL-BRIEFS. When the caller's last session is older than 3 days, one short card from " +
      "Recall's catch-up read as the caller. Never an error: memory off, not set up for them, or " +
      "not offered by Recall yet all answer a state with no card.",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: CatchUpCard },
    errors: [],
    emits: [],
  }),
  "memory.catchUpDismiss": defineRoute({
    ...memory,
    id: "memory.catchUpDismiss",
    method: "post",
    path: `${base}/me/catch-up/dismiss`,
    summary: "Close the catch-up card for its window (never shown again for it)",
    access: "member",
    params: CompanyParams,
    body: CatchUpDismiss,
    response: { status: 200, schema: CatchUpCard },
    errors: ["validation_failed"],
    emits: ["memory.catch_up_closed"],
  }),
  "promotions.list": defineRoute({
    ...memory,
    id: "promotions.list",
    method: "get",
    path: `${base}/promotions`,
    summary: "Promotions (Recall page candidates) the caller reviews, newest first",
    access: "member",
    params: CompanyParams,
    query: PromotionListQuery,
    response: { status: 200, schema: page(PromotionItem) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "promotions.accept": defineRoute({
    ...memory,
    id: "promotions.accept",
    method: "post",
    path: `${base}/promotions/{id}/accept`,
    summary: "Accept a promotion: it becomes a Knowledge page in a space",
    description:
      "The caller decides the promotion (a holder of its reviewer seat, an owner when it has " +
      "none, or a manager) and may write in the space. The page is created from the proposal's " +
      "text (`promoted_from_memory_ref` names the Recall proposal), the promotion is `accepted` " +
      "with its page, and its approval (when it has one) is approved and executed. A promotion " +
      "already decided is 409.",
    access: "member",
    params: CompanyItemParams,
    body: PromotionAccept,
    idempotency: "optional",
    response: { status: 200, schema: PromotionDecided },
    errors: ["conflict"],
    emits: ["page.created", "promotion.accepted", "approval.decided"],
  }),
  "promotions.reject": defineRoute({
    ...memory,
    id: "promotions.reject",
    method: "post",
    path: `${base}/promotions/{id}/reject`,
    summary: "Reject a promotion (no page); its approval is rejected too",
    access: "member",
    params: CompanyItemParams,
    body: PromotionReject,
    idempotency: "optional",
    response: { status: 200, schema: PromotionDecided },
    errors: ["conflict"],
    emits: ["promotion.rejected", "approval.decided"],
  }),
  "recall.status": defineRoute({
    ...memory,
    id: "recall.status",
    method: "get",
    path: `${base}/recall/status`,
    summary: "Setup › Recall: connection, tenant, callback registration and agent keys",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: RecallStatus },
    errors: [],
    emits: [],
  }),
  // RECALL-KB: spaces synced to Recall knowledge bases (the worker's `recall.kb_sync` sends them)
  "recall.kbList": defineRoute({
    ...memory,
    id: "recall.kbList",
    method: "get",
    path: `${base}/recall/kb`,
    summary: "Setup › Recall: every space, its Sync to Recall switch, agent access and last sync",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: RecallKbList },
    errors: [],
    emits: [],
  }),
  "recall.kbUpdate": defineRoute({
    ...memory,
    id: "recall.kbUpdate",
    method: "put",
    path: `${base}/recall/kb/{id}`,
    summary: "Switch a space's Sync to Recall on or off, and set what agents may do in it",
    description:
      "`id` is a space (not a personal one). On: the worker ensures the space's Recall knowledge " +
      "base and syncs its pages (one batched sync, at most every `interval_minutes`); the DEs " +
      "whose seat reads the space get `read` or `read_write` on it. Off: no more syncs and agents " +
      "lose it; what Recall holds stays until a page is deleted.",
    access: "manager",
    params: CompanyItemParams,
    body: RecallKbUpdate,
    response: { status: 200, schema: RecallKbSpace },
    errors: ["conflict"],
    emits: ["recall.kb_configured"],
  }),
  "recall.kbSyncNow": defineRoute({
    ...memory,
    id: "recall.kbSyncNow",
    method: "post",
    path: `${base}/recall/kb/{id}/sync`,
    summary: "Sync a switched-on space at the worker's next minute, past the interval",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: RecallKbSpace },
    errors: ["conflict"],
    emits: ["recall.kb_configured"],
  }),
  // D8 create/move dialog
  "recall.reachPreview": defineRoute({
    ...memory,
    id: "recall.reachPreview",
    method: "post",
    path: `${base}/recall/reach-preview`,
    summary: "What agents will read in memory after a proposed seat or team change (D8 dialog)",
    description:
      "Read only: nothing is changed. Resolved by the same rule the Recall sync sends (every agent " +
      "reads the company level; a department's spaces only with a seat in that department), " +
      "before and after the proposed change. Lists every agent whose reach changes, and the DE " +
      "the change names even when it does not. Old department access drops by itself; keeping " +
      "it is not offered (a cross-department exception, D10, is undecided). 404 when an id is " +
      "not this company's; 409 when the change itself would be refused (a DE given a second " +
      "seat; a seat joining a sub-team it is not in the parent of; a team moved under a parent " +
      "that lacks its members), 422 `org_chart_cycle` for a team moved under itself.",
    access: "manager",
    params: CompanyParams,
    body: MemoryReachChange,
    safe: true,
    response: { status: 200, schema: MemoryReachPreview },
    errors: ["conflict", "org_chart_cycle"],
    emits: [],
  }),
  // D15 (preview): an agent's typed memory write, budgeted per run
  "memory.agentRemember": defineRoute({
    ...memory,
    id: "memory.agentRemember",
    method: "post",
    path: `${base}/agent/memory`,
    summary: "An agent run writes one typed, one-sentence memory with evidence (D15 preview)",
    description:
      "On by default; 404 (as if the route did not exist) only where the company set " +
      "`settings.memory.agent_discipline.enabled` to false. A digital employee's own running run (its run token, or " +
      "its agent key with `X-CompanyOS-Run`). `kind` is lesson, decision (advisory), pattern, " +
      "fact or correction; `sentence` is ONE sentence of at most 280 characters, no line " +
      "breaks, never a transcript, a prompt or tool output; `why` (required) says why it is " +
      "worth remembering in one line; `evidence` is this run, or a task or message of this " +
      "company. 422 `validation_failed` with a code per problem: `not_one_sentence`, " +
      "`too_long`, `looks_like_tool_output`, `contains_secret`, `empty`, `missing_why`, " +
      "`not_a_decision` (a decision names who decided), `not_in_vocabulary` (the kind is not a " +
      "type the company's memory takes), `duplicate_of` (the seat's memory already holds it; " +
      "the error names the record), `evidence_not_found`. MEMORY-SKILL-2: no per-run cap. A " +
      "lesson is queued at once as an unconfirmed lesson only its own seat is served, with a " +
      "`memory_lesson` approval for the seat's supervisor that shares it with the team (or " +
      "the company) once approved; the others are queued for the run's seat memory (sent " +
      "within minutes). Every accepted write is a step in the run's timeline.",
    access: "run_token",
    params: CompanyParams,
    body: AgentMemoryWriteBody,
    idempotency: "optional",
    response: { status: 200, schema: AgentMemoryWritten },
    errors: ["run_not_owned", "run_state_conflict", "key_unbound"],
    emits: ["run.step_recorded", "approval.requested"],
  }),
  // RECALL-RULES: standing rules people state and DEs suggest; Recall holds them as Constraints
  "memoryRules.list": defineRoute({
    ...memory,
    id: "memoryRules.list",
    method: "get",
    path: `${base}/memory/rules`,
    summary: "The company's standing rules, or the ones that bind one DE (with its automations)",
    description:
      "Every member reads the rules (they bind the company's agents). `de_id`: the company's " +
      "rules and that DE's own, plus the board automations the DE owns (B1-DE-RULES) on boards " +
      "the caller can view. `status=current` (default) lists active and proposed rules.",
    access: "member",
    params: CompanyParams,
    query: MemoryRuleListQuery,
    response: { status: 200, schema: MemoryRuleList },
    errors: [],
    emits: [],
  }),
  "memoryRules.create": defineRoute({
    ...memory,
    id: "memoryRules.create",
    method: "post",
    path: `${base}/memory/rules`,
    summary: "State a standing rule for every agent or one DE (an owner or admin), or propose one",
    description:
      "An owner or admin states it: it binds at once, every run of the agents it binds is told it " +
      "at wake-up, and the worker sets it in Recall as a Constraint of the company's global space " +
      "(as that person). Anyone else proposes it: a `memory_rule` approval for the company's " +
      "owners (Inbox); it binds once approved. `chat`: `message_id` is the message it was made " +
      "from (one the caller can read). A `de` rule names a DE of this company (`de_id`).",
    access: "member",
    params: CompanyParams,
    body: MemoryRuleCreate,
    idempotency: "optional",
    response: { status: 201, schema: MemoryRuleView },
    errors: ["conflict"],
    emits: ["memory_rule.changed", "approval.requested"],
  }),
  "memoryRules.lift": defineRoute({
    ...memory,
    id: "memoryRules.lift",
    method: "post",
    path: `${base}/memory/rules/{id}/lift`,
    summary: "Lift an active rule (an owner or admin); it is kept as history and lifted in Recall",
    access: "manager",
    params: CompanyItemParams,
    body: MemoryRuleLift,
    idempotency: "optional",
    response: { status: 200, schema: MemoryRuleView },
    errors: ["conflict"],
    emits: ["memory_rule.changed"],
  }),
  "memory.agentSuggestRule": defineRoute({
    ...memory,
    id: "memory.agentSuggestRule",
    method: "post",
    path: `${base}/agent/rules`,
    summary: "A DE suggests a standing rule (for itself or every agent) for a person to approve",
    description:
      "A digital employee's own running run (its run token, or its seat-bound agent key with " +
      "`X-CompanyOS-Run`). Always a proposal: a `memory_rule` approval for the company's owners, " +
      "then the DE's chain. Approved, it binds and goes to Recall; the DE is never told it binds " +
      "before that. Logged as a step of the run.",
    access: "run_token",
    params: CompanyParams,
    body: AgentRuleSuggestion,
    idempotency: "optional",
    response: { status: 201, schema: AgentRuleSuggested },
    errors: ["run_not_owned", "run_state_conflict", "key_unbound"],
    emits: ["memory_rule.changed", "approval.requested", "run.step_recorded"],
  }),
  // RECALL-PROCEDURES: before repeatable work, the procedure; after it, the run (failures too)
  "memory.agentProcedureFor": defineRoute({
    ...memory,
    id: "memory.agentProcedureFor",
    method: "post",
    path: `${base}/agent/procedures/for`,
    summary: "The procedure to follow for a repeatable task (Recall's, and the DE's playbooks)",
    description:
      "A digital employee's own running run. Asks Recall's `procedure_for` with the DE's own " +
      "memory key (what it can read), and matches the playbooks its seat owns or follows by " +
      "title. Nothing is written. Follow the steps, then record the run with " +
      "`agent/procedures/runs` (failed and abandoned runs too).",
    access: "run_token",
    params: CompanyParams,
    body: AgentProcedureFor,
    safe: true,
    response: { status: 200, schema: AgentProcedureOffers },
    errors: ["run_not_owned", "run_state_conflict", "key_unbound"],
    emits: [],
  }),
  "memory.agentProcedureRun": defineRoute({
    ...memory,
    id: "memory.agentProcedureRun",
    method: "post",
    path: `${base}/agent/procedures/runs`,
    summary: "Record one run of a procedure: how it went and each step (failures too)",
    description:
      "A digital employee's own running run. Kept in CompanyOS (the history beside the playbook " +
      "the procedure is) and sent to Recall's run record under the DE's own key, where Recall's " +
      "run rates and vetoes learn from it. `recall_state` says whether Recall took it. A " +
      "`bucket_id` that is not one of the company's spaces in Recall is 404.",
    access: "run_token",
    params: CompanyParams,
    body: AgentProcedureRunBody,
    idempotency: "optional",
    response: { status: 201, schema: AgentProcedureRunRecorded },
    errors: ["run_not_owned", "run_state_conflict", "key_unbound", "not_found"],
    emits: ["procedure.run_recorded", "run.step_recorded"],
  }),
  // RECALL-RUN-LIFECYCLE: what memory hands a run at wake; a carry note when it ends with work left
  "memory.agentWake": defineRoute({
    ...memory,
    id: "memory.agentWake",
    method: "post",
    path: `${base}/agent/memory/wake`,
    summary: "What memory hands a run at wake: the brief, where it left off, its procedure",
    description:
      "A digital employee's own running run (its runtime calls it once, at wake). Recall's " +
      "context about the run's subject (its task's title and board, or the message), sized by " +
      "the operator's `memory.brief_chars`; the DE's own working state for that subject when " +
      "under 7 days old; and, when the task's kind maps to a procedure family (publish, send, " +
      "outreach, deploy, report, data entry, onboarding), the procedure that fits. Every Recall " +
      "call names the run (`X-Recall-Run-Id`). Kept on the run; the procedure handed is filed as " +
      "a procedure run when the run ends if the DE filed none.",
    access: "run_token",
    params: CompanyParams,
    body: AgentMemoryWakeBody,
    idempotency: "optional",
    response: { status: 200, schema: AgentMemoryWake },
    errors: ["run_not_owned", "run_state_conflict", "key_unbound"],
    emits: ["run.step_recorded"],
  }),
  "memory.agentCarry": defineRoute({
    ...memory,
    id: "memory.agentCarry",
    method: "post",
    path: `${base}/agent/memory/carry`,
    summary: "A run ending with work left writes where it stands (the DE's working state)",
    description:
      "A digital employee's own running run, written by its runtime when the run times out or is " +
      "lost: the DE's working state in Recall for the run's subject (next step, what it had done), " +
      "read back at its next wake. Not memory: never extracted, the DE's own.",
    access: "run_token",
    params: CompanyParams,
    body: AgentMemoryCarryBody,
    idempotency: "optional",
    response: { status: 200, schema: AgentMemoryCarried },
    errors: ["run_not_owned", "run_state_conflict", "key_unbound"],
    emits: ["run.step_recorded"],
  }),
  // RECALL-UPKEEP: Setup › Recall › Memory upkeep (dreams and hot copy)
  "recall.upkeep": defineRoute({
    ...memory,
    id: "recall.upkeep",
    method: "get",
    path: `${base}/recall/upkeep`,
    summary: "Setup › Recall: dream and hot copy settings, and whether Recall took them",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: RecallUpkeep },
    errors: [],
    emits: [],
  }),
  // RECALL-INBOX-LOOP ---------------------------------------------------------------------
  "memory.lane": defineRoute({
    ...memory,
    id: "memory.lane",
    method: "get",
    path: `${base}/memory/lane`,
    summary: "The Inbox's Memory lane: everything waiting for the caller's yes or no on memory",
    description:
      "Agent lessons, page candidates, dream and rule proposals, contradictions (with Recall's " +
      "settle suggestion) and decisions due their 'did it work' check. Who sees a card is decided " +
      "at request time: the holder of the space's department seat, or the person an agent " +
      "serves; it comes up to the owner/CEO when the department has no human, its head is a DE, " +
      "or nobody answered within the escalation window (3 days, operator data, a company " +
      "overrides it in settings.memory.lane). A company with no humans below the owner sends " +
      "everything to the owner. The owner/CEO's 'See all agents' memory' switch (default off) " +
      "opens every agent's items company-wide, never a person's personal space.",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: MemoryLane },
    errors: [],
    emits: [],
  }),
  "memory.laneDecide": defineRoute({
    ...memory,
    id: "memory.laneDecide",
    method: "post",
    path: `${base}/memory/lane/decide`,
    summary: "Answer one Memory lane card: approve, edit, reject, or a decision's check",
    description:
      "A person who sees the card answers it (403 for an agent). A lesson is decided at once; a " +
      "dream proposal's approve and reject too. What needs a Recall door that is not live yet " +
      "(an edited approval, a rule's confirm or reject, a decision's answer as the person) is " +
      "kept and answered `waiting`: it is sent as that person once the memory service takes it " +
      "(retried daily), never an error. A contradiction is settled with " +
      "`memory.conflictSettle`; a page candidate with `promotions.accept`.",
    access: "member",
    params: CompanyParams,
    body: MemoryLaneDecide,
    idempotency: "optional",
    response: { status: 200, schema: MemoryLaneDecided },
    errors: ["forbidden", "conflict", "not_found"],
    emits: ["memory_lane.decided", "decision.outcome_answered"],
  }),
  "memory.laneBulk": defineRoute({
    ...memory,
    id: "memory.laneBulk",
    method: "post",
    path: `${base}/memory/lane/bulk-approve`,
    summary: "Approve everything in the Memory lane except contradictions",
    description:
      "Every open card the caller sees whose kind is in `kinds` (default all): lessons, dream " +
      "proposals, rule proposals (kept for the memory service), page candidates that sit in a " +
      "space. Contradictions are never batched, and neither are decisions' checks.",
    access: "member",
    params: CompanyParams,
    body: MemoryLaneBulk,
    idempotency: "optional",
    response: { status: 200, schema: MemoryLaneBulked },
    errors: ["forbidden"],
    emits: ["memory_lane.bulk_approved", "memory_lane.decided"],
  }),
  "memory.laneSeeAll": defineRoute({
    ...memory,
    id: "memory.laneSeeAll",
    method: "put",
    path: `${base}/memory/lane/see-all`,
    summary: "Owner/CEO: switch 'See all agents' memory' on or off",
    description:
      "Default off. On: the lane and Memory browse show every agent's memory items company-wide " +
      "(read and approve); a person's personal space stays out. 403 for anyone but an owner or " +
      "the CEO. Each change is audited.",
    access: "member",
    params: CompanyParams,
    body: MemoryLaneSeeAll,
    idempotency: "optional",
    response: {
      status: 200,
      schema: z.object({ on: z.boolean() }).meta({ id: "MemoryLaneSeeAllSet" }),
    },
    errors: ["forbidden"],
    emits: ["memory_lane.see_all_changed"],
  }),
  "memory.decisionCalibration": defineRoute({
    ...memory,
    id: "memory.decisionCalibration",
    method: "get",
    path: `${base}/memory/decisions/calibration`,
    summary: "How this quarter's decisions held up (admin insights)",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: DecisionCalibration },
    errors: [],
    emits: [],
  }),
  // RECALL-ADMIN-MEMORY ---------------------------------------------------------------------
  "memory.adminOverview": defineRoute({
    ...memory,
    id: "memory.adminOverview",
    method: "get",
    path: `${base}/memory/admin`,
    summary: "Setup › Memory: whether the memory service takes admin reads, and the tab counts",
    description:
      "Owners and admins. `service_ready` is whether Recall lists the `admin` act (not live yet; " +
      "asked of Recall, §7.1 (A)); until then every tab shows what CompanyOS holds and says " +
      "'Waiting on memory service' for the rest, never an error.",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: MemoryAdminOverview },
    errors: [],
    emits: [],
  }),
  "memory.adminBrowse": defineRoute({
    ...memory,
    id: "memory.adminBrowse",
    method: "get",
    path: `${base}/memory/admin/browse`,
    summary: "Browse: the spaces the caller may see, the map of one, and its records",
    description:
      "Shared spaces (company, departments, teams, spaces) and the agent spaces the caller " +
      "reaches by the Memory lane's routing: their department's, the agents that serve them, " +
      "and, with no person above, an owner's or the CEO's. An owner or the CEO who switched on " +
      "'See all agents' memory' sees every agent's space. A person's personal space is never " +
      "listed, whoever asks. Records come from the memory service when it takes admin reads " +
      "and from what agents told memory (kept here) until then.",
    access: "manager",
    params: CompanyParams,
    query: MemoryAdminBrowseQuery,
    response: { status: 200, schema: MemoryAdminBrowse },
    errors: [],
    emits: [],
  }),
  "memory.adminRecord": defineRoute({
    ...memory,
    id: "memory.adminRecord",
    method: "get",
    path: `${base}/memory/admin/record`,
    summary: "One record with its versions, trace, expansion, chains and timeline",
    access: "manager",
    params: CompanyParams,
    query: MemoryAdminRecordQuery,
    response: { status: 200, schema: MemoryAdminRecordPage },
    errors: ["not_found"],
    emits: [],
  }),
  "memory.adminRecordAct": defineRoute({
    ...memory,
    id: "memory.adminRecordAct",
    method: "post",
    path: `${base}/memory/admin/record/act`,
    summary: "Flag, archive or restore one record (through the install doors, live now)",
    description:
      "Flag puts the record in its space's review queue; archive takes it out of what agents " +
      "recall; restore brings an archived one back. The record's space must be one the caller " +
      "sees. A Recall that cannot take it answers `waiting` or `refused` with the reason.",
    access: "manager",
    params: CompanyParams,
    body: MemoryAdminRecordAct,
    idempotency: "optional",
    response: { status: 200, schema: MemoryAdminActed },
    errors: ["not_found"],
    emits: ["memory_admin.acted"],
  }),
  "memory.adminSpaces": defineRoute({
    ...memory,
    id: "memory.adminSpaces",
    method: "get",
    path: `${base}/memory/admin/spaces`,
    summary: "Spaces: charter, agent access and who knows what, per space",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: MemoryAdminSpaces },
    errors: [],
    emits: [],
  }),
  "memory.adminCharter": defineRoute({
    ...memory,
    id: "memory.adminCharter",
    method: "put",
    path: `${base}/memory/admin/spaces/{space}/charter`,
    summary: "Set a space's charter (as the caller, through the memory service)",
    access: "manager",
    params: MemoryAdminSpaceParams,
    body: MemoryAdminCharterSet,
    idempotency: "optional",
    response: { status: 200, schema: MemoryAdminActed },
    errors: ["not_found"],
    emits: ["memory_admin.acted"],
  }),
  "memory.adminAgentAccess": defineRoute({
    ...memory,
    id: "memory.adminAgentAccess",
    method: "put",
    path: `${base}/memory/admin/spaces/{space}/agent-access`,
    summary: "Set what agents may do in a space: nothing, read, or read and write",
    access: "manager",
    params: MemoryAdminSpaceParams,
    body: MemoryAdminAccessSet,
    idempotency: "optional",
    response: { status: 200, schema: MemoryAdminActed },
    errors: ["not_found"],
    emits: ["memory_admin.acted"],
  }),
  "memory.adminRules": defineRoute({
    ...memory,
    id: "memory.adminRules",
    method: "get",
    path: `${base}/memory/admin/rules`,
    summary:
      "Rules: each space's always-on tier, standing rules with their fence, and procedure steps",
    description:
      "From the memory service (the `admin` act); the company's own standing rules are the " +
      "separate `memoryRules.list`. Before the act is live: `service_ready` false and no spaces.",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: MemoryAdminRules },
    errors: [],
    emits: [],
  }),
  "memory.adminRuleAct": defineRoute({
    ...memory,
    id: "memory.adminRuleAct",
    method: "post",
    path: `${base}/memory/admin/rules/act`,
    summary: "Tend a space's rules: always-on, retire, lift, veto a procedure step",
    description:
      "Answers `waiting` (nothing changed) while the memory service does not take the admin " +
      "act.",
    access: "manager",
    params: CompanyParams,
    body: MemoryAdminRuleAct,
    idempotency: "optional",
    response: { status: 200, schema: MemoryAdminActed },
    errors: ["not_found"],
    emits: ["memory_admin.acted"],
  }),
  "memory.adminHealth": defineRoute({
    ...memory,
    id: "memory.adminHealth",
    method: "get",
    path: `${base}/memory/admin/health`,
    summary: "Health: what agents save, what waits, and the memory service's noise numbers",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: MemoryAdminHealth },
    errors: [],
    emits: [],
  }),
  "memory.adminJobs": defineRoute({
    ...memory,
    id: "memory.adminJobs",
    method: "get",
    path: `${base}/memory/admin/jobs`,
    summary: "Jobs filed for the operator (batch settle, clearing injection flags)",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: MemoryAdminJobs },
    errors: [],
    emits: [],
  }),
  "memory.adminJobFile": defineRoute({
    ...memory,
    id: "memory.adminJobFile",
    method: "post",
    path: `${base}/memory/admin/jobs`,
    summary: "File a job for the operator: settle open pairs in bulk, or clear injection flags",
    description:
      "Recall keeps these signed-in only, so the click files a job (with the UI event id) and " +
      "the operator does it in Recall. The same job filed twice while one is open is the same " +
      "job.",
    access: "manager",
    params: CompanyParams,
    body: MemoryAdminJobFile,
    idempotency: "optional",
    response: { status: 200, schema: MemoryAdminJobFiled },
    errors: ["not_found"],
    emits: ["memory_admin.job_changed"],
  }),
  // PLAN-CHECK: a DE's run checks a plan against past decisions and rules
  "memory.agentPlanCheck": defineRoute({
    ...memory,
    id: "memory.agentPlanCheck",
    method: "post",
    path: `${base}/agent/plan-check`,
    summary: "An agent run checks a plan against past decisions and rules (Recall plan_check)",
    description:
      "404 (as if the route did not exist) where the company set " +
      "`settings.memory.agent_discipline.enabled` to false. A digital employee's own running " +
      "run (its run token, or its agent key with `X-CompanyOS-Run`). Recall's `plan_check` is " +
      "asked with the DE's OWN Recall key over the spaces it reads (at most 6), and the worst " +
      "verdict is answered: `conflict` means stop and ask your human. Recall not connected, the " +
      "DE's key not held yet, or Recall not answering is `unavailable` with `detail`, never an " +
      "error. Every check is a step in the run's timeline and is kept: the approval the DE " +
      "raises next in this run carries it on its card.",
    access: "run_token",
    params: CompanyParams,
    body: AgentPlanCheckBody,
    response: { status: 200, schema: AgentPlanChecked },
    errors: ["run_not_owned", "run_state_conflict", "key_unbound"],
    emits: ["run.step_recorded"],
  }),
  // M7-05
  "digitalEmployees.recallAgent": defineRoute({
    feature: "memory",
    id: "digitalEmployees.recallAgent",
    method: "get",
    path: `${base}/digital-employees/{id}/recall-agent`,
    tag: "memory",
    summary: "The DE's Recall agent: key state, granted buckets, and its hire-as-DE memory copy",
    description:
      "Read as the DE's profile (the DE seat's viewers; managers when it has no seat). Never " +
      "returns the key: `state` says whether it is held, `key_for_prefix` which CompanyOS agent " +
      "key it follows. `not_configured` while Recall is not wired to this deployment.",
    access: "seat_viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: RecallAgentState },
    errors: [],
    emits: [],
  }),
} as const;
